import { useState } from "react";
import { useDisplay } from "@/context/DisplayContext";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Paintbrush, X, Sun, Moon, Contrast, RotateCcw } from "lucide-react";

export default function DisplayToolbar() {
  const [open, setOpen] = useState(false);
  const { fontSize, zoom, theme, compact, setFontSize, setZoom, setTheme, setCompact, resetAll } = useDisplay();

  const themes = [
    { id: "light", icon: Sun, label: "Light" },
    { id: "dark", icon: Moon, label: "Dark" },
    { id: "high-contrast", icon: Contrast, label: "HC" },
  ];

  return (
    <>
      {/* Trigger button — bottom-right corner */}
      {!open && (
        <button
          data-testid="display-toolbar-trigger"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[100] w-10 h-10 rounded-full flex items-center justify-center shadow-lg border border-gray-200/50 transition-all hover:scale-105 active:scale-95"
          style={{
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            fontSize: "14px",
          }}
          title="Display settings"
        >
          <Paintbrush className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
        </button>
      )}

      {/* Backdrop */}
      {open && (
        <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
      )}

      {/* Drawer panel — slides from right */}
      <div
        data-testid="display-toolbar"
        className={`fixed top-0 right-0 z-[101] h-full w-72 border-l shadow-2xl transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          fontSize: "14px",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Paintbrush className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
            <span className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-700">Display</span>
          </div>
          <button onClick={() => setOpen(false)} className="p-1 rounded-sm hover:bg-gray-100 transition-colors" data-testid="display-drawer-close">
            <X className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-5 space-y-6 overflow-y-auto" style={{ height: "calc(100% - 56px)" }}>
          {/* Theme tiles */}
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold block mb-2">Theme</label>
            <div className="grid grid-cols-3 gap-2">
              {themes.map((t) => {
                const Icon = t.icon;
                const active = theme === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`theme-${t.id}`}
                    onClick={() => setTheme(t.id)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-sm border text-xs transition-all hover:scale-[1.02] ${
                      active
                        ? t.id === "dark" ? "bg-gray-800 text-white border-gray-700 shadow-md" :
                          t.id === "high-contrast" ? "bg-black text-yellow-400 border-yellow-500 shadow-md" :
                          "bg-gray-900 text-white border-gray-800 shadow-md"
                        : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    <span className="font-medium">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Font size */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Text Size</label>
              <span className="font-mono-data text-xs font-semibold text-gray-700" data-testid="font-size-label">{fontSize}%</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 font-heading">A</span>
              <Slider
                data-testid="drawer-font-slider"
                value={[fontSize]}
                onValueChange={(v) => setFontSize(v[0])}
                min={75} max={125} step={5}
                className="flex-1"
              />
              <span className="text-base text-gray-400 font-heading font-semibold">A</span>
            </div>
          </div>

          {/* Zoom */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Page Zoom</label>
              <span className="font-mono-data text-xs font-semibold text-gray-700" data-testid="zoom-label">{zoom}%</span>
            </div>
            <Slider
              data-testid="drawer-zoom-slider"
              value={[zoom]}
              onValueChange={(v) => setZoom(v[0])}
              min={75} max={125} step={5}
            />
            <div className="flex justify-between text-[10px] text-gray-400 font-mono-data mt-1">
              <span>75%</span><span>100%</span><span>125%</span>
            </div>
          </div>

          {/* Compact view */}
          <div className="flex items-center justify-between border border-gray-200 rounded-sm p-3">
            <div>
              <span className="text-sm font-medium text-gray-700 block">Compact View</span>
              <span className="text-[11px] text-gray-400">Tighter spacing, smaller text</span>
            </div>
            <Switch
              data-testid="compact-toggle"
              checked={compact}
              onCheckedChange={setCompact}
            />
          </div>

          {/* Reset */}
          <div className="pt-2 border-t border-gray-200">
            <button
              data-testid="display-reset"
              onClick={() => { resetAll(); }}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1.5"
            >
              <RotateCcw className="w-3 h-3" strokeWidth={1.5} />
              Restore defaults
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
